import React, { createContext, useContext } from 'react';
import { Link } from 'react-router-dom';

/* Renders "*words*" in the brand green; everything else inherits the colour. */
export const Highlight = ({ text, accent = 'text-brand-ink' }) => {
  if (!text) return null;
  return String(text)
    .split(/(\*[^*]+\*)/g)
    .filter(Boolean)
    .map((part, i) =>
      part.startsWith('*') && part.endsWith('*') ? (
        <span key={i} className={accent}>
          {part.slice(1, -1)}
        </span>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
};

/* Plain-text paragraphs separated by newlines. */
export const Paragraphs = ({ text, className = '' }) =>
  String(text || '')
    .split('\n')
    .filter((p) => p.trim())
    .map((p, i) => (
      <p key={i} className={className}>
        {p}
      </p>
    ));

/* Admin uploads are stored as "/api/uploads/…" on the backend; everything else is a public path or full URL. */
export const assetUrl = (url) =>
  url && url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const sitemapUrl = () => `${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`;

/*
 * A link target as stored in the admin:
 *   "register" → opens the sign-up modal
 *   "login"    → opens the login modal
 *   "sitemap"  → backend sitemap.xml
 *   "/path"    → client-side route
 *   anything else (https:, mailto:) → normal anchor
 */
/* Pages wrapped in <PageShell> provide the auth modal opener here, so buttons don't need it passed down. */
export const AuthModalContext = createContext(null);

export const SmartLink = ({ to, onAuth: onAuthProp, className = '', children, ...rest }) => {
  const ctxAuth = useContext(AuthModalContext);
  const onAuth = onAuthProp || ctxAuth;
  if (to === 'register' || to === 'login') {
    return (
      <button type="button" onClick={() => onAuth?.(to)} className={className} {...rest}>
        {children}
      </button>
    );
  }
  if (to === 'sitemap') {
    return (
      <a href={sitemapUrl()} target="_blank" rel="noopener noreferrer" className={className} {...rest}>
        {children}
      </a>
    );
  }
  if (to && to.startsWith('/')) {
    return (
      <Link to={to} className={className} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={to || '#'} className={className} {...rest}>
      {children}
    </a>
  );
};

export const BrandButton = ({ to, onAuth, variant = 'solid', className = '', arrow = true, children, ...rest }) => (
  <SmartLink to={to} onAuth={onAuth} className={`hp-btn hp-btn-${variant} ${className}`} {...rest}>
    {children}
    {arrow && <span aria-hidden className="hp-arrow" />}
  </SmartLink>
);

/* Outer div takes margins/colour; the inner line keeps Figma's 12px box on one line but wraps legibly. */
export const Kicker = ({ children, className = '' }) => (
  <div className={className}>
    <p className="hp-kicker hp-kicker-wrap">{children}</p>
  </div>
);

/* The hand-drawn arrow between sections. */
export const Squiggle = ({ className = '' }) => (
  <img
    src="/brand/squiggle.svg"
    alt=""
    aria-hidden
    className={`mx-auto block h-[clamp(110px,9.2222vw,166px)] w-[clamp(40px,3.3333vw,60px)] ${className}`}
  />
);
